import './SuperAdminCourseTemplates.scss';
import React, {useContext} from 'react';
import {I18nReactContext} from "../../../contexts/I18nContext";
import {DataGridWrapper} from "../../../Utils/DataGridWrapper/DataGridWrapper";
import {GridReactContextProvider, GridReactContext} from "../../../Utils/DataGridWrapper/GridContext";
import {useNavigate} from 'react-router-dom';
import {
    SA_getAllPublicTemplatesAPI,
    SA_deletePublicTemplateAPI,
    SA_createPublicTemplateAPI,
    SA_duplicatePublicTemplateAPI
} from '../../../Services/SA_PublicTemplates_api';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {map} from 'lodash';
import {Button} from "../../Form/Button/Button";
import {buttonColors} from "../../Pickers/InputTheme";
import '../SuperAdminPage.scss';

const getColumns = async ({i18n, gridContext}) => {
    // Delete template
    const handleDelete = async (id) => {
        if (window.confirm(i18n.translate('superAdmin.courseTemplates.confirmDelete', {
            defaultValue: 'Are you sure you want to delete this template?'
        }))) {
            try {
                await SA_deletePublicTemplateAPI({id});
                // Trigger grid refresh after successful deletion
                if (gridContext && gridContext.triggerTableRefresh) {
                    gridContext.triggerTableRefresh();
                }
            } catch (error) {
                console.error('Error deleting template:', error);
                alert(i18n.translate('superAdmin.courseTemplates.deleteError', {
                    defaultValue: 'Failed to delete template. Please try again later.'
                }));
            }
        }
    };

    // Edit template
    const handleEdit = (id) => {
        window.location.href = `/nihol/courseTemplateEditor/${id}/edit`;
    };

    // View template
    const handleView = (id) => {
        window.location.href = `/weezbeeCourse/${id}`;
    };

    /**
     * Handle template duplication
     * Automatically duplicates template with "copy_" prefix
     * The backend generates the new name automatically
     *
     * @param {string} id - Template ID to duplicate
     */
    const handleDuplicate = async (id) => {
        try {
            // Call backend API to perform duplication
            await SA_duplicatePublicTemplateAPI({ id });

            // Trigger grid refresh to show the newly duplicated template
            if (gridContext && gridContext.triggerTableRefresh) {
                gridContext.triggerTableRefresh();
            }
        } catch (error) {
            console.error('Error duplicating template:', error);
            alert(i18n.translate('superAdmin.courseTemplates.duplicateError', {
                defaultValue: 'Failed to duplicate template. Please try again later.'
            }));
        }
    };

    return [
        {
            field: 'templateName',
            headerName: i18n.translate('superAdmin.courseTemplates.columns.templateName'),
            minWidth: 250,
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}}>{params.value}</div>,
        },
        {
            field: 'description',
            headerName: i18n.translate('superAdmin.courseTemplates.columns.description'),
            minWidth: 250,
            sortable: false,
            renderCell: (params) => (
                <div className="template-description-cell">
                    {params.value ? params.value.substring(0, 100) + (params.value.length > 100 ? '...' : '') : ''}
                </div>
            )
        },
        {
            field: 'isPubliclyAccessible',
            headerName: i18n.translate('superAdmin.courseTemplates.columns.isPubliclyAccessible'),
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <div className={`template-status ${params.value ? 'public' : 'private'}`}>
                    {params.value ? 'Yes' : 'No'}
                </div>
            )
        },
        {
            field: 'isReady',
            headerName: i18n.translate('superAdmin.courseTemplates.columns.isReady'),
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <div className={`template-status ${params.value ? 'ready' : 'draft'}`}>
                    {params.value ? 'Yes' : 'No'}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: i18n.translate('superAdmin.courseTemplates.columns.actions'),
            width: 300,
            sortable: false,
            renderCell: (params) => (
                <div className="template-actions">
                    <Button
                        icon={<EditIcon />}
                        onClick={() => handleEdit(params.row.id)}
                        size="small"
                        color={buttonColors.BLUE}
                        title={i18n.translate('superAdmin.courseTemplates.buttons.editTemplate')}
                    />
                    <Button
                        icon={<ContentCopyIcon />}
                        onClick={() => handleDuplicate(params.row.id)}
                        size="small"
                        color={buttonColors.BLUE}
                        title={i18n.translate('superAdmin.courseTemplates.buttons.duplicateTemplate')}
                    />
                    <Button
                        icon={<DeleteIcon />}
                        onClick={() => handleDelete(params.row.id)}
                        size="small"
                        color={buttonColors.RED}
                        title={i18n.translate('superAdmin.courseTemplates.buttons.deleteTemplate')}
                    />
                </div>
            )
        }
    ];
};

const convertData = (response) => {
    // Handle both array response and object response with data property
    const templates = Array.isArray(response) ? response : (response?.data || response);
    return map(templates, (template) => {
        return template;
    });
};

const paginationModel = {
    page: 0,
    pageSize: 10,
};

const filterModel = {
    filterValues: {},
    filterData: {},
    showFilters: {
        search: true
    }
};

const SuperAdminCourseTemplatesInner = () => {
    const {i18n} = useContext(I18nReactContext);
    const gridContext = useContext(GridReactContext);
    const navigate = useNavigate();

    // Create new template
    const handleCreate = async () => {
        try {
            // Create a new template with default values
            const newTemplate = await SA_createPublicTemplateAPI({
                templateName: 'New Template',
                templateSubject: 'General',
                description: '',
                isPubliclyAccessible: false,
                isReady: false,
                tags: [],
                estimatedDuration: 60,
                sections: []
            });

            // Navigate to the course editor with the new template ID
            if (newTemplate && newTemplate.id) {
                const templateId = String(newTemplate.id);
                navigate(`/nihol/courseTemplateEditor/${templateId}/edit`);
            } else {
                throw new Error('Failed to create template: No ID returned');
            }
        } catch (error) {
            console.error('Error creating new template:', error);
            alert(i18n.translate('superAdmin.courseTemplates.createError', {
                defaultValue: 'Failed to create new template. Please try again later.'
            }));
        }
    };

    const apiToGetData = async ({paginationModel, filterModel}) => {
        return SA_getAllPublicTemplatesAPI({paginationModel, filterModel});
    };

    const buttons = [
        {
            text: i18n.translate('superAdmin.courseTemplates.buttons.createTemplate'),
            icon: <AddIcon />,
            color: buttonColors.BLUE,
            onClick: handleCreate
        }
    ];

    const getColumnsWrapper = async (params) => {
        return getColumns({...params, i18n, gridContext});
    };

    return <DataGridWrapper
        filterModel={filterModel}
        disableColumnFilter
        getColumns={getColumnsWrapper}
        paginationModel={paginationModel}
        apiToGetData={apiToGetData}
        dataConverter={convertData}
        buttons={buttons}
    />
};

export const SuperAdminCourseTemplates = () => {
    return <div className="SuperAdminCourseTemplates SuperAdminPage">
        <GridReactContextProvider>
            <SuperAdminCourseTemplatesInner />
        </GridReactContextProvider>
    </div>
};
